// @ts-check
import {
  paymentService,
  bgProfileAddressUCAService,
  bgProfilePaymentUCAService,
  nmProfileAddressUCAService,
  nmProfilePaymentUCAService,
  getProfileAttributesUcaService,
  accountAssociateHierarchyService,
  wishlistService,
} from 'config/api';

import { splitAndMapAddressesByType, mapUcaPaymentsToCfa, mapUcaProfileAttributesToCfa } from 'utils';

/**
 * @typedef {import('../../cart/types').PaymentDTO} PaymentDTO
 * @typedef {import('types').UCAPaymentDTO} UCAPaymentDTO
 * @typedef {import('types').UCAAddressDTO} UCAAddressDTO
 */

/**
 * @param {PaymentDTO} payment
 * @param {string} clientId
 */
export const zeroDollarAuth = async (payment, clientId) => {
  try {
    const { data } = await paymentService.post(
      '/submitPayment',
      {
        oneTimeToken: payment.onTimeToken,
        billingAddress: {
          addressLine1: payment.billingAddress.line1,
          addressLine2: payment.billingAddress.line2,
          country: payment.billingAddress.country,
          city: payment.billingAddress.city,
          firstName: payment.billingAddress.firstName,
          lastName: payment.billingAddress.lastName,
          zip: payment.billingAddress.zip,
          state: payment.billingAddress.state,
        },
        cardType: payment.networkType,
        productTotalAmount: 0.0,
      },
      {
        httpClientOptions: { handleErrors: false },
        clientId,
      },
    );

    const isPLC = payment.networkType === 'PLC';

    return {
      data: {
        keyIdentifier: data.keyIdentifier,
        invoiceNumber: data.invoiceNumber,
        aurusPayTicketNumber: data.aurusPayTicketNumber,
        /**
         * TODO: here we put subType in networkType for correct display of icon
         * but it may leeds to errors, because further we use networkType as cartType for payment.
         * Rewrite this and use right cardType for payment and separate subType field for icon display
         */
        networkType: isPLC ? data.aurusPaymentInformation.subCardType : payment.networkType,
      },
    };
  } catch (err) {
    // @ts-ignore
    const errMsg = err.response?.data?.message || 'Failed zero payment check. Try another card.';
    return { error: errMsg };
  }
};

/**
 * @param {object} clientId
 */
export const getClientProfileDataFromUCA = async ({
  clientId,
  isBGAssociate,
  associatePin,
  isBGFarfetchEnabled = false,
}) => {
  const ucaAddressService = isBGAssociate ? bgProfileAddressUCAService : nmProfileAddressUCAService;
  const ucaProfileAttributesService = getProfileAttributesUcaService(isBGAssociate);

  // fetch and map client profile attributes
  const { data } = await ucaProfileAttributesService.get(`${associatePin}/customers/${clientId}/attributes`);
  const profileAttributes = mapUcaProfileAttributesToCfa(data);

  // fetch and map client addresses
  /** @type {{ data: UCAAddressDTO[] }} */
  const { data: ucaAddresses } = await ucaAddressService.get(`/${clientId}/addresses`);
  const { billingAddress, shippingAddress } = splitAndMapAddressesByType(
    ucaAddresses,
    isBGAssociate,
    isBGFarfetchEnabled,
  );

  const ucaPaymentsService = isBGAssociate ? bgProfilePaymentUCAService : nmProfilePaymentUCAService;
  // fetch and map client payments
  /** @type {{ data: UCAPaymentDTO[] }} */
  const { data: ucaPayments } = await ucaPaymentsService.get(`/${clientId}/payments`);
  const payments = mapUcaPaymentsToCfa(ucaPayments, billingAddress);

  return { shippingAddress, billingAddress, payments, profileAttributes };
};

/**
 * @param {string} clientId
 * @param {string} title
 * @param {object} productData
 */
export const createWishlistForClient = async (clientId, title = '', productData) => {
  try {
    const { data } = await wishlistService.post(`/${clientId}/wishlists`, { title, items: [productData] });
    return { data };
  } catch (error) {
    return { error };
  }
};

/**
 * @param {string} clientId
 * @param {string} wishlistId
 * @param {object} productData
 */
export const addProductToWishlist = async (clientId, wishlistId, productData) => {
  try {
    const { data } = await wishlistService.post(`/${clientId}/wishlists/${wishlistId}/items`, productData);
    return { data };
  } catch (error) {
    return { error };
  }
};

/**
 * @param {string} clientId
 */
export const getClientWishlists = async (clientId) => {
  try {
    const { data } = await wishlistService.get(`/${clientId}/wishlists`);
    return { data };
  } catch (error) {
    return { error };
  }
};

/**
 * @param {string} clientId
 * @param {string} wishlistId
 */
export const getClientSingleWishlist = async (clientId, wishlistId) => {
  try {
    const { data } = await wishlistService.get(`/${clientId}/wishlists/${wishlistId}`);
    return { data };
  } catch (error) {
    return { error };
  }
};

/**
 * @param {string} associateId
 */
export const getStoreMapping = async (associateId = '') => {
  try {
    const { data } = await accountAssociateHierarchyService.get(`/associate/assignment/${associateId}`);
    return data;
  } catch (err) {
    return null;
  }
};
